import {Button, Select, Spin} from 'antd'
import {organization_fetchStatistics_Api} from 'apis'
import classNames from 'classnames'
import Pure from 'components/Pure'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {IoChevronDown,} from 'react-icons/io5'
import {nest, withProps} from 'recompose'
import {DelayRender} from 'views/Discovery/DelayRender'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {deepTranslate, getResponseItem, NullAsDefault, renderSelf} from 'views/Shared'
import WorkspaceContext, {WorkspaceFilterParams,} from 'views/Workspace/WorkspaceContext'
import WorkspaceInsightsChart, {InsightsChartTypes,} from './WorkspaceInsightsChart'
import WorkspaceInsightsMetaSummary from './WorkspaceInsightsMetaSummary'
import {PiArticleNyTimes, PiCalendarDots, PiChalkboardTeacher, PiExam, PiUsers} from "react-icons/pi";
import WorkspaceOrganizationAnnouncements from "./WorkspaceOrganizationAnnouncements";
import CRUD from "../../Shared/enums/CRUD";
import {useOrganizationAnnouncementApi} from "../../AppLocals/Organization/functions/useOrganizationAnnouncementApi";

export const SummaryFilterOptions = Object.freeze({
    YESTERDAY: {
        label: 'yesterday',
        value: 'YESTERDAY',
    },
    TODAY: {
        label: 'today',
        value: 'TODAY',
    },
    LAST7DAYS: {
        label: 'last 7 days',
        value: 'LAST7DAYS',
    },
    THISMONTH: {
        label: 'this month',
        value: 'THISMONTH',
    },
    LASTMONTH: {
        label: 'last month',
        value: 'LASTMONTH',
    },
})

export const InsightsBlockHeader = ({
                                        title,
                                        style,
                                        children,
                                        className,
                                        renderTitle = renderSelf,
                                    }) => (
    <div
        style={style}
        className={classNames('flex items-center justify-between mb-3', className)}>
        <Translate>
            {(t) => (
                <div className="text-color-200 uppercase font-medium text-lg">
                    {renderTitle(t(title))}
                </div>
            )}
        </Translate>
        {children}
    </div>
)

export const PeriodFilterHeader = ({
                                       title,
                                       value,
                                       defaultValue,
                                       onSelect = NullAsDefault,
                                       options = [
                                           SummaryFilterOptions.YESTERDAY,
                                           SummaryFilterOptions.TODAY,
                                           SummaryFilterOptions.LAST7DAYS,
                                           SummaryFilterOptions.THISMONTH,
                                           SummaryFilterOptions.LASTMONTH,
                                       ],
                                   }) => {
    const t = useTranslate()

    const hasFilter = !_.isEmpty(options)

    const _options = useMemo(() => {
        return Array.from(options).map(deepTranslate(t))
    }, [t, options])

    return (
        <InsightsBlockHeader title={title}>
            {hasFilter && (
                <Select
                    size="small"
                    value={value}
                    options={_options}
                    onSelect={onSelect}
                    style={{width: 110}}
                    // defaultValue={defaultValue}
                    className="text-xs text-color-300"
                    suffixIcon={<IoChevronDown className="text-color-300"/>}
                />
            )}
        </InsightsBlockHeader>
    )
}

const InsightsCharts = ({
                            style,
                            data = [],
                            className,
                            isLoading = false,
                            organization_id,
                        }) => {
    const [chart_1, chart_2] = [
        InsightsChartTypes.TYPE_1,
        InsightsChartTypes.TYPE_2,
    ].map((name) => ['workspace', organization_id, name].join('/'))
    return (
        <div
            style={style}
            className={className}>
            <Spin spinning={!!isLoading}>
                <WorkspaceInsightsChart
                    type={InsightsChartTypes.TYPE_1}
                    chartInfo={{
                        id: chart_1,
                        idname: chart_1,
                        data_values: Array.from(data).map(({log_date, ...rest}) => ({
                            time: log_date,
                            articles: rest.total_articles,
                            asks: rest.total_asks,
                            polls: rest.total_polls,
                            reposts: rest.total_reposts,
                        })),
                        data_columns: [
                            {
                                id: 'time',
                                dataIndex: 'time',
                                type: 'date',
                            },
                            {
                                id: 'articles',
                                dataIndex: 'articles',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'asks',
                                dataIndex: 'asks',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'polls',
                                dataIndex: 'polls',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'reposts',
                                dataIndex: 'reposts',
                                type: 'number',
                                valueType: 'value',
                            },
                        ],
                    }}
                />
            </Spin>
            <Spin spinning={!!isLoading}>
                <WorkspaceInsightsChart
                    type={InsightsChartTypes.TYPE_2}
                    chartInfo={{
                        id: chart_2,
                        idname: chart_2,
                        data_values: Array.from(data).map(({log_date, ...rest}) => ({
                            time: log_date,
                            views: rest.total_views,
                            votes: rest.total_votes,
                            follows: rest.total_follows,
                            comments: rest.total_comments,
                        })),
                        data_columns: [
                            {
                                id: 'time',
                                dataIndex: 'time',
                                type: 'date',
                            },
                            {
                                id: 'views',
                                dataIndex: 'views',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'votes',
                                dataIndex: 'votes',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'follows',
                                dataIndex: 'follows',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'comments',
                                dataIndex: 'comments',
                                type: 'number',
                                valueType: 'value',
                            },
                        ],
                    }}
                />
            </Spin>
        </div>
    )
}

const WorkspaceInsights = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {
        organization_id,
        organization,
        getPeriod = NullAsDefault,
        setPeriod = NullAsDefault,
    } = useContext(WorkspaceContext)
    const organizationDepartments = _.get(organization, 'organization_departments', [])

    const period = getPeriod(WorkspaceFilterParams.PERIOD)

    const {
        modal,
        refresh_token,
        handleAction = NullAsDefault,
    } = useOrganizationAnnouncementApi({
        organization,
        organizationDepartments,
        notifyListeners: {}
    })

    return (
        <Wrapper className="flex flex-col space-y-6">
            {/* Thống kê */}
            <Async
                {...{
                    apiInfo: organization_fetchStatistics_Api,
                    query: bindQueryParam({
                        id: organization_id,
                    }),
                    values: {
                        period,
                    },
                }}>
                {({response, isLoading, handleAsyncAction = NullAsDefault}) => {
                    const {data, summary, organization_summary} = getResponseItem(response)
                    return (
                        <React.Fragment>
                            <Pure input={[period, summary, isLoading, handleAsyncAction]}>
                                <WorkspaceInsightsMetaSummary
                                    summary={organization_summary}
                                    isLoading={isLoading}
                                    className="grid grid-cols-2 md:grid-cols-4 gap-4"
                                    Header={({title}) => (
                                        <PeriodFilterHeader
                                            title={title}
                                            value={period}
                                            onSelect={(period) => {
                                                setPeriod(WorkspaceFilterParams.PERIOD, period)
                                                handleAsyncAction({
                                                    values: {
                                                        period,
                                                    },
                                                })
                                            }}
                                        />
                                    )}
                                    field_params={[
                                        {
                                            name: 'total_members',
                                            label: 'total members',
                                            extra_name: 'total_members_prev',
                                            icon: <PiUsers size={20} className="text-indigo-500"/>,
                                        },
                                        {
                                            name: 'total_examinations',
                                            label: 'total number of exams',
                                            extra_name: 'total_examinations_prev',
                                            icon: <PiExam size={22} className="text-indigo-500"/>,
                                        },
                                        {
                                            name: 'total_courses',
                                            label: 'total courses',
                                            extra_name: 'total_courses_prev',
                                            icon: (
                                                <PiChalkboardTeacher size={22} className="text-indigo-500"/>
                                            ),
                                        },
                                        {
                                            name: 'total_events',
                                            label: 'total events',
                                            extra_name: 'total_events_prev',
                                            icon: <PiCalendarDots size={20} className="text-indigo-500"/>,
                                        },
                                        {
                                            name: 'total_posts',
                                            label: 'total posts',
                                            extra_name: 'total_posts_prev',
                                            icon: <PiArticleNyTimes size={20} className="text-indigo-500"/>,
                                        },
                                    ]}
                                />
                            </Pure>

                        </React.Fragment>
                    )
                }}
            </Async>

            <div className="flex flex-col space-y-3">
                <div className="mt-4 flex items-center justify-between gap-2">
                    <h2 className="flex-1 text-color-200 uppercase font-medium text-lg">
                        {t('announcements')}
                    </h2>
                    <Button
                        color="primary"
                        variant="filled"
                        className="button-rounded-md no-border flex flex-center font-medium text-xs md:text-sm"
                        onClick={() => {
                            handleAction(CRUD.CREATE);
                        }}>
                        {t("create")}
                    </Button>
                </div>
                <WorkspaceOrganizationAnnouncements refreshToken={refresh_token}/>
            </div>
            <div className="h-20"/>
            {modal}
        </Wrapper>
    )
}

export default nest(withProps({lazy: true})(DelayRender), WorkspaceInsights)
